/**
 * TransactionsSection 交易紀錄 (SR3.4, plan §WR3 — DD!159–168, all 0708-J Must).
 *
 * The Related-Records Transactions grid, upgraded from the S1.4 SO/PO view to
 * the Must contract: a 檢視 View (direction) filter + a 類型 Type multi-select
 * over the four document types the ERP owns — SO / PO / 進貨 goods-receipt
 * docket / 發票 customer invoice — with every row drilling to its source
 * document. This is a **Tier-2 aggregate over ERP-owned documents**, NOT
 * NetSuite's transaction ledger; the arithmetic lives in the pure
 * `transactionsSelector` (one record, many lenses — nothing stored).
 *
 * Columns (DD!162–168): Date · Type · Document # · Name (交易對象) · Quantity ·
 * Rate 單價 · Amount 金額. Numeric columns are neutral and width-bounded.
 * LOADING (skeleton) + hard-ERROR (retry) states are defined up front,
 * alongside the empty and filtered-empty states.
 *
 * 進貨 rows read the S5.1 DOCKET contract (`@/types/api/goodsReceipt`) — the
 * shape `GET /goods-receipts` actually serves (`goodsReceiptHandlers` shadow
 * the retired codex-era route; api-spec `listGoodsReceipts` declares it).
 * Reading the retired `@/types/api/shipping` `GoodsReceipt` here blanked 單號
 * (DD!164), 數量 (DD!166) and 金額 (DD!168) on every 進貨 row.
 */

import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Select, Skeleton, Table, Tag, theme } from 'antd';
import type { GlobalToken, TableProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useRequest } from 'ahooks';
import TableContainer, {
  ListEmptyState,
  ListErrorState,
  renderDash,
} from '@/components/common/TableContainer';
import PurpleTag from '@/components/common/PurpleTag';
import { TimestampDisplay } from '@/components/common/TimestampDisplay';
import { formatMoney, formatQuantity } from '@/components/salesOrders/components/soFormat';
import { purchaseOrderApi, salesOrderApi } from '@/services/api';
import apiClient from '@/services/api/axios.config';
import { API_ROUTES } from '@/constants/api.constants';
import {
  buildProductTransactions,
  directionOf,
  filterProductTransactions,
} from '@/lib/reports/transactionsSelector';
import type {
  ProductTransactionRow,
  ProductTransactionType,
  ProductTransactionView,
} from '@/lib/reports/transactionsSelector';
import type { GoodsReceiptListResponse } from '@/types/api/goodsReceipt';
import type { CustomerInvoice } from '@/types/api/finance';
import type { Product } from '@/types/api/product';
import SectionHeader from './SectionHeader';

interface TransactionsSectionProps {
  product: Product;
}

/** Tabular-numeric span so figure columns align and stay width-bounded. */
const numeric = { fontVariantNumeric: 'tabular-nums' as const };

/** The four document types, in grid order — options for the 類型 multi-select. */
const TYPE_ORDER: ProductTransactionType[] = ['so', 'po', 'goodsReceipt', 'invoice'];

/* eslint-disable react-refresh/only-export-components -- the column contract
   and the presentational grid are data/component exports consumed by this
   slice's tests and the S8.2 closure test (registry-as-data doctrine). */

/**
 * The seven rendered column keys. Written as `Extract<...>` so renaming a
 * `ProductTransactionRow` field breaks the contract at compile time instead of
 * quietly rendering a blank column.
 */
export type ProductTransactionColumnKey = Extract<
  keyof ProductTransactionRow,
  'date' | 'type' | 'docNumber' | 'counterpartyName' | 'qty' | 'unitAmount' | 'amount'
>;

/** One DD!162–168 grid column, as data. */
export interface ProductTransactionColumnSpec {
  /** `ProductTransactionRow` field this column reads — also the dataIndex. */
  key: ProductTransactionColumnKey;
  /** Data Dictionary row (0708-J Must) this column discharges. */
  ddRow: number;
  /** products-namespace i18n key of the header label. */
  labelKey: string;
  width?: number;
  align?: 'right';
}

/**
 * The DD!162–168 column contract, in DD order — registry-as-data so the S8.2
 * closure test can credit each xlsx row against the RENDERED grid instead of
 * grepping this file for a docblock string (a grep credit survived deleting
 * the column it credited).
 */
export const PRODUCT_TRANSACTION_COLUMNS: readonly ProductTransactionColumnSpec[] = [
  { key: 'date', ddRow: 162, labelKey: 'detail.transactions.date', width: 140 },
  { key: 'type', ddRow: 163, labelKey: 'detail.transactions.docType', width: 130 },
  { key: 'docNumber', ddRow: 164, labelKey: 'detail.transactions.docNumber', width: 180 },
  { key: 'counterpartyName', ddRow: 165, labelKey: 'detail.transactions.counterparty' },
  { key: 'qty', ddRow: 166, labelKey: 'detail.transactions.qty', width: 100, align: 'right' },
  {
    key: 'unitAmount',
    ddRow: 167,
    labelKey: 'detail.transactions.unitAmount',
    width: 150,
    align: 'right',
  },
  { key: 'amount', ddRow: 168, labelKey: 'detail.transactions.amount', width: 150, align: 'right' },
] as const;

const renderMoney = (value: number | null, row: ProductTransactionRow): ReactNode =>
  value === null ? renderDash(null) : formatMoney(row.currency, value);

/** Per-column cell renderers, keyed by the contract's `key`. */
const CELL_RENDERERS: Record<
  ProductTransactionColumnSpec['key'],
  (row: ProductTransactionRow, ctx: { t: TFunction; token: GlobalToken }) => ReactNode
> = {
  date: (row) =>
    row.date ? <TimestampDisplay date={row.date} showLabel={false} /> : renderDash(null),
  // Type carries a text label (never colour alone); sales-direction docs reuse
  // the SO purple convention, purchase-direction docs a neutral tag.
  type: (row, { t }) => {
    const label = t(`products:detail.transactions.types.${row.type}`);
    return directionOf(row.type) === 'sales' ? <PurpleTag>{label}</PurpleTag> : <Tag>{label}</Tag>;
  },
  docNumber: (row, { token }) => (
    <span style={{ color: token.colorPrimary, fontWeight: token.fontWeightStrong }}>
      {row.docNumber}
    </span>
  ),
  counterpartyName: (row) => renderDash(row.counterpartyName),
  // 數量 — house thousands grouping, same as every other numeric column.
  qty: (row) => <span style={numeric}>{formatQuantity(row.qty)}</span>,
  unitAmount: (row) => <span style={numeric}>{renderMoney(row.unitAmount, row)}</span>,
  amount: (row) => <span style={numeric}>{renderMoney(row.amount, row)}</span>,
};

/**
 * Build the antd columns FROM the DD!162–168 contract — one definition, so a
 * deleted column disappears from the rendered grid and the closure test alike.
 */
export function buildProductTransactionColumns(
  t: TFunction,
  token: GlobalToken
): NonNullable<TableProps<ProductTransactionRow>['columns']> {
  return PRODUCT_TRANSACTION_COLUMNS.map((column) => ({
    title: t(`products:${column.labelKey}`),
    dataIndex: column.key,
    key: column.key,
    width: column.width,
    align: column.align,
    render: (_value: unknown, row: ProductTransactionRow) =>
      CELL_RENDERERS[column.key](row, { t, token }),
  }));
}

export interface ProductTransactionsGridProps {
  rows: readonly ProductTransactionRow[];
  onRowClick: (row: ProductTransactionRow) => void;
}

/**
 * The 交易紀錄 grid itself — presentational, so the DD!162–168 columns can be
 * rendered (and asserted) without the section's four fetches.
 */
export const ProductTransactionsGrid = ({ rows, onRowClick }: ProductTransactionsGridProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const columns = useMemo(() => buildProductTransactionColumns(t, token), [t, token]);

  return (
    <TableContainer>
      <Table<ProductTransactionRow>
        rowKey="key"
        size="small"
        dataSource={rows}
        columns={columns}
        pagination={rows.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
        onRow={(row) => ({
          onClick: () => onRowClick(row),
          style: { cursor: 'pointer' },
        })}
        locale={{
          emptyText: <ListEmptyState title={t('products:detail.transactions.states.noResults')} />,
        }}
        scroll={{ x: 1050 }}
      />
    </TableContainer>
  );
};

/* eslint-enable react-refresh/only-export-components */

const TX_SO_CACHE_KEY = '/api/sales-orders?view=product-transactions';
const TX_PO_CACHE_KEY = '/api/purchase-orders?view=product-transactions';
const TX_GR_CACHE_KEY = '/api/goods-receipts?view=product-transactions';
const TX_INVOICE_CACHE_KEY = '/api/finance/customer-invoices?view=product-transactions';

export const TransactionsSection = ({ product }: TransactionsSectionProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const navigate = useNavigate();

  const [view, setView] = useState<ProductTransactionView>('all');
  const [types, setTypes] = useState<ProductTransactionType[]>([]);

  const ready = !!product.id;
  const options = { ready, staleTime: 30_000 };

  // Four ERP-owned source stores — SO / PO / goods-receipt docket / customer
  // invoice. Each is fetched independently so a single failure is a defined
  // hard-ERROR (retry) rather than a discovered blank.
  const so = useRequest(async () => (await salesOrderApi.list({ pageSize: 100 })).data, {
    ...options,
    cacheKey: TX_SO_CACHE_KEY,
  });
  const po = useRequest(async () => (await purchaseOrderApi.list({ pageSize: 100 })).data, {
    ...options,
    cacheKey: TX_PO_CACHE_KEY,
  });
  // 進貨 — the S5.1 docket list envelope declared by api-spec `listGoodsReceipts`
  // and served by `goodsReceiptHandlers`; NOT the retired shipping shape.
  const gr = useRequest(
    async () =>
      (await apiClient.get<GoodsReceiptListResponse>(API_ROUTES.GOODS_RECEIPTS)).data.data ?? [],
    { ...options, cacheKey: TX_GR_CACHE_KEY }
  );
  const invoices = useRequest(
    async () =>
      (await apiClient.get<{ data: CustomerInvoice[] }>('/finance/customer-invoices')).data.data,
    { ...options, cacheKey: TX_INVOICE_CACHE_KEY }
  );

  const isLoading = so.loading || po.loading || gr.loading || invoices.loading;
  const error = so.error || po.error || gr.error || invoices.error;

  const refreshAll = () => {
    so.refresh();
    po.refresh();
    gr.refresh();
    invoices.refresh();
  };

  const allRows = useMemo(
    () =>
      buildProductTransactions(product.id, {
        salesOrders: so.data ?? [],
        purchaseOrders: po.data ?? [],
        goodsReceipts: gr.data ?? [],
        customerInvoices: invoices.data ?? [],
      }),
    [product.id, so.data, po.data, gr.data, invoices.data]
  );

  const rows = useMemo(
    () => filterProductTransactions(allRows, { view, types }),
    [allRows, view, types]
  );

  const drillPath = (row: ProductTransactionRow): string => {
    switch (row.type) {
      case 'so':
        return `/auth/sales-orders/${row.docId}`;
      case 'po':
        return `/auth/purchase-orders/${row.docId}`;
      case 'goodsReceipt':
        return '/auth/goods-receipts';
      case 'invoice':
        return `/auth/finance/invoices/customer/${row.docId}`;
    }
  };

  const header = (
    <SectionHeader
      title={t('products:detail.sections.transactions')}
      subtitle={t('products:detail.transactions.subtitle')}
    />
  );

  // LOADING — skeleton up front (defined state, never a discovered flash).
  if (isLoading) {
    return (
      <div>
        {header}
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  // hard-ERROR — replace the body with a retry panel (any source failing).
  if (error) {
    return (
      <div>
        {header}
        <ListErrorState
          title={t('products:detail.transactions.states.loadFailed')}
          retryLabel={t('products:detail.transactions.states.retry')}
          onRetry={refreshAll}
        />
      </div>
    );
  }

  // Empty — no document references this product at all.
  if (allRows.length === 0) {
    return (
      <div>
        {header}
        <ListEmptyState title={t('products:detail.transactions.empty')} />
      </div>
    );
  }

  return (
    <div>
      {header}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: token.marginSM,
          marginBottom: token.marginSM,
        }}
      >
        <Select<ProductTransactionView>
          size="large"
          value={view}
          onChange={setView}
          style={{ width: 180, maxWidth: '100%' }}
          aria-label={t('products:detail.transactions.filters.view')}
          options={[
            { value: 'all', label: t('products:detail.transactions.filters.allViews') },
            { value: 'sales', label: t('common:menu.sales') },
            { value: 'purchases', label: t('common:menu.procurement') },
          ]}
        />
        <Select<ProductTransactionType[]>
          size="large"
          mode="multiple"
          allowClear
          value={types}
          onChange={setTypes}
          style={{ width: 280, maxWidth: '100%' }}
          placeholder={t('products:detail.transactions.filters.allTypes')}
          aria-label={t('products:detail.transactions.filters.type')}
          options={TYPE_ORDER.map((type) => ({
            value: type,
            label: t(`products:detail.transactions.types.${type}`),
          }))}
        />
      </div>

      <ProductTransactionsGrid rows={rows} onRowClick={(row) => navigate(drillPath(row))} />
    </div>
  );
};

export default TransactionsSection;
